import React, { useEffect, useRef, useState } from 'react';
import { LuArrowDown, LuArrowUp, LuCheck, LuPencil, LuPlus, LuTrash2, LuX } from 'react-icons/lu';
import { EditorState, ContentState, convertToRaw } from 'draft-js';
import { Editor } from 'react-draft-wysiwyg';
import draftToMarkdown from 'draftjs-to-markdown';
import htmlToDraft from 'html-to-draftjs';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import '../../App.css';
import { API_BASE_URL } from '../../config.js';
import { cx } from '../../utils/cx.js';
import Markdown from '../ui/Markdown';
import Button from '../ui/Button';
import IconButton from '../ui/IconButton';
import Alert from '../ui/Alert';
import ConfirmModal from '../ui/ConfirmModal';
import { INPUT_CLASS } from '../ui/Field';
import { SectionShell, sectionAnchor } from './AnalysisDisplay';
import { analysisContent, analysisPrompt, isCustomAnalysis, toEntries } from './analysisContent.js';

// Clean content formatting
const cleanContent = (content) => {
  if (!content) return '';
  return content
    .replace(/\\s/g, ' ')
    .replace(/\\n/g, '\n')
    .replace(/[ \t]+/g, ' ')
    .replace(/\n /g, '\n')
    .replace(/ \n/g, '\n')
    .replace(/:\s*\n-/g, ':\n\n-')
    .replace(/\n-/g, '\n- ')
    .trim();
};

const TOOLBAR = {
  options: ['inline', 'list'],
  inline: { options: ['bold', 'italic', 'underline'] },
  list: { options: ['unordered', 'ordered'] },
};

/**
 * Admin editor of an analysis: rename/add/delete categories, edit/reorder/delete points.
 * Every change is saved immediately (PATCH); edits land in custom_analysis, the AI output is kept.
 */
export const AnalysisEditor = ({ analysis, onSave, clientSlug, weekId, fetchWithAuth }) => {
  const isCustom = isCustomAnalysis(analysis);

  const [editMode, setEditMode] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editorState, setEditorState] = useState(null);
  const [editingSectionTitle, setEditingSectionTitle] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  const [sectionToDelete, setSectionToDelete] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const isMounted = useRef(true);
  const draftRef = useRef(null);
  useEffect(() => {
    isMounted.current = true;
    return () => { isMounted.current = false; };
  }, []);

  // The caret lands in the editor as soon as it opens.
  useEffect(() => {
    if (!editMode) return undefined;
    const t = setTimeout(() => draftRef.current?.focus?.(), 0);
    return () => clearTimeout(t);
  }, [editMode, editingCategory, editingIndex]);

  const getDisplayAnalysis = () => analysisContent(analysis);
  const getCurrentEntries = (sectionTitle) => getDisplayAnalysis()[sectionTitle] || [];

  // Custom analyses keep their prompt next to the categories: { prompt, analyse }.
  const withCategories = (categories) => {
    const updated = { ...analysis };
    updated.custom_analysis = isCustom ? { prompt: analysisPrompt(analysis), analyse: categories } : categories;
    return updated;
  };

  const patchAnalysis = async (updatedAnalysis) => {
    setSaving(true);
    setSaveError(null);
    try {
      const response = await fetchWithAuth(
        `${API_BASE_URL}/api/weekly/client/${clientSlug}/week/${weekId}/analyses/${analysis.type}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...updatedAnalysis, custom_analysis: updatedAnalysis.custom_analysis || {} }),
        },
      );
      if (!response.ok) throw new Error('Failed to update analysis');
      onSave(updatedAnalysis);
      return true;
    } catch (error) {
      console.error('Error updating analysis:', error);
      if (isMounted.current) setSaveError('La modification n\'a pas pu être enregistrée. Réessayez.');
      return false;
    } finally {
      if (isMounted.current) setSaving(false);
    }
  };

  const updateSection = (sectionTitle, entries) => {
    const categories = { ...getDisplayAnalysis(), [sectionTitle]: entries };
    return patchAnalysis(withCategories(categories));
  };

  const openEditor = (category, index, content = '') => {
    setEditMode(true);
    setEditingCategory(category);
    setEditingIndex(index);
    try {
      const contentBlock = htmlToDraft(`<p>${content}</p>`);
      setEditorState(contentBlock
        ? EditorState.createWithContent(ContentState.createFromBlockArray(contentBlock.contentBlocks))
        : EditorState.createEmpty());
    } catch (error) {
      console.error('Error converting content to draft state:', error);
      setEditorState(EditorState.createEmpty());
    }
  };

  const handleEdit = (category, index) => {
    const entries = toEntries(getDisplayAnalysis()[category]);
    openEditor(category, index, entries[index] || '');
  };

  const handleCancel = () => {
    setEditMode(false);
    setEditingCategory(null);
    setEditingIndex(null);
    setEditorState(null);
  };

  const handleSave = () => {
    const markdownContent = cleanContent(draftToMarkdown(convertToRaw(editorState.getCurrentContent())));
    if (!markdownContent) {
      handleCancel();
      return;
    }
    const entries = toEntries(getCurrentEntries(editingCategory));
    if (editingIndex === -1) entries.push(markdownContent);
    else if (editingIndex >= 0 && editingIndex < entries.length) entries[editingIndex] = markdownContent;
    const category = editingCategory;
    handleCancel();
    updateSection(category, entries);
  };

  const handleSectionTitleSave = async (oldTitle) => {
    const newTitle = editingValue.trim();
    if (!newTitle || newTitle === oldTitle) {
      setEditingSectionTitle(null);
      return;
    }
    // Rebuilt in place so the renamed category keeps its position.
    const categories = Object.fromEntries(Object.entries(getDisplayAnalysis()).map(([k, v]) => [k === oldTitle ? newTitle : k, v]));
    if (await patchAnalysis(withCategories(categories))) setEditingSectionTitle(null);
    else setEditingValue(oldTitle);
  };

  const handleReorder = (sectionTitle, fromIndex, toIndex) => {
    const entries = toEntries(getCurrentEntries(sectionTitle));
    const [removed] = entries.splice(fromIndex, 1);
    entries.splice(toIndex, 0, removed);
    updateSection(sectionTitle, entries);
  };

  const handleDeleteItem = (sectionTitle, index) => {
    const entries = toEntries(getCurrentEntries(sectionTitle));
    entries.splice(index, 1);
    updateSection(sectionTitle, entries);
  };

  const handleDeleteSection = async () => {
    const categories = { ...getDisplayAnalysis() };
    delete categories[sectionToDelete];
    if (await patchAnalysis(withCategories(categories))) setSectionToDelete(null);
  };

  const renderEditor = (submitLabel) => (
    <div className="space-y-3">
      <div className="editor-container">
        <Editor
          editorRef={(ref) => { draftRef.current = ref; }}
          editorState={editorState}
          onEditorStateChange={(state) => isMounted.current && setEditorState(state)}
          editorClassName="editor-input"
          toolbar={TOOLBAR}
        />
      </div>
      <div className="flex justify-end gap-2">
        <Button size="sm" icon={LuX} onClick={handleCancel}>Annuler</Button>
        <Button size="sm" variant="primary" icon={LuCheck} onClick={handleSave}>{submitLabel}</Button>
      </div>
    </div>
  );

  const categories = Object.entries(getDisplayAnalysis());

  return (
    <div className="space-y-4">
      {saveError && <Alert tone="danger">{saveError}</Alert>}

      {categories.length === 0 && (
        <p className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">Cette analyse ne contient aucun élément.</p>
      )}

      {categories.map(([sectionTitle, bullets], sectionIndex) => {
        const entries = toEntries(bullets);
        const id = sectionAnchor(analysis.type, sectionIndex);
        const addingHere = editMode && editingCategory === sectionTitle && editingIndex === -1;
        return (
          <SectionShell
            key={sectionTitle}
            id={id}
            count={entries.length}
            title={editingSectionTitle === sectionTitle ? (
              <input
                type="text"
                value={editingValue}
                aria-label="Titre de la section"
                onChange={(e) => setEditingValue(e.target.value)}
                onBlur={() => handleSectionTitleSave(sectionTitle)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSectionTitleSave(sectionTitle);
                  else if (e.key === 'Escape') { e.preventDefault(); setEditingSectionTitle(null); }
                }}
                className={cx(INPUT_CLASS, '!py-1 font-semibold')}
                autoFocus
              />
            ) : (
              <h3
                id={`${id}-title`}
                className="text-sm font-semibold text-gray-900 dark:text-white"
                onDoubleClick={() => { setEditingSectionTitle(sectionTitle); setEditingValue(sectionTitle); }}
              >
                {sectionTitle}
              </h3>
            )}
            actions={(
              <div className="flex flex-shrink-0 items-center gap-0.5">
                <IconButton icon={LuPlus} size="xs" label={isCustom ? 'Ajouter un champ' : 'Ajouter un point'} onClick={() => openEditor(sectionTitle, -1)} />
                <IconButton icon={LuPencil} size="xs" label="Renommer la section" onClick={() => { setEditingSectionTitle(sectionTitle); setEditingValue(sectionTitle); }} />
                <IconButton icon={LuTrash2} size="xs" variant="danger" label="Supprimer la section" onClick={() => setSectionToDelete(sectionTitle)} />
              </div>
            )}
          >
            <ul className="divide-y divide-gray-100 dark:divide-gray-900">
              {entries.map((entry, entryIndex) => (
                <li key={entryIndex} className="group relative px-4 py-3.5">
                  {editMode && editingCategory === sectionTitle && editingIndex === entryIndex ? renderEditor('Enregistrer') : (
                    <>
                      {entry.trim()
                        ? <Markdown className="md:pr-32">{entry}</Markdown>
                        : <p className="text-sm italic text-gray-400">Point vide — modifiez-le ou supprimez-le.</p>}
                      <div className="mt-2 flex gap-0.5 transition-opacity md:absolute md:right-2 md:top-2 md:mt-0 md:rounded-lg md:border md:border-gray-200 md:bg-white md:p-0.5 md:opacity-0 md:shadow-sm md:group-hover:opacity-100 md:focus-within:opacity-100 dark:md:border-gray-800 dark:md:bg-night-750">
                        <IconButton icon={LuPencil} size="xs" label="Modifier" onClick={() => handleEdit(sectionTitle, entryIndex)} disabled={saving} />
                        <IconButton icon={LuArrowUp} size="xs" label="Monter" onClick={() => handleReorder(sectionTitle, entryIndex, entryIndex - 1)} disabled={saving || entryIndex === 0} />
                        <IconButton icon={LuArrowDown} size="xs" label="Descendre" onClick={() => handleReorder(sectionTitle, entryIndex, entryIndex + 1)} disabled={saving || entryIndex === entries.length - 1} />
                        <IconButton icon={LuTrash2} size="xs" variant="danger" label="Supprimer ce point" onClick={() => handleDeleteItem(sectionTitle, entryIndex)} disabled={saving} />
                      </div>
                    </>
                  )}
                </li>
              ))}
              {addingHere && (
                <li className="px-4 py-3.5">{renderEditor(isCustom ? 'Ajouter le champ' : 'Ajouter le point')}</li>
              )}
            </ul>
          </SectionShell>
        );
      })}

      <ConfirmModal
        isOpen={sectionToDelete !== null}
        onClose={() => setSectionToDelete(null)}
        onConfirm={handleDeleteSection}
        loading={saving}
        title="Supprimer la section ?"
        message={<>La section <strong className="text-gray-900 dark:text-white">« {sectionToDelete} »</strong> et tous ses points seront retirés de l'analyse.</>}
        confirmLabel="Supprimer la section"
      />
    </div>
  );
};

export default AnalysisEditor;
